import "./modal.scss";

import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import FocusLock from "react-focus-lock";

import platformIdentifiers from "../../constants/platformIdentifiers";
import Dimensions from "../../constants/styles/dimensions";
import AppShared from "../../features/app/App.shared";
import SmartErrorBoundary from "../../features/smartErrorBoundary";
import { immutableRecords, PropTypeShapes } from "../../types";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { ContainerContext, withContainerContext } from "../container/ContainerContext";
import { asOptionalPortalChild } from "../portal/asPortalChild";
import { withScreenContext } from "../screen/ScreenContext";
import { ModalSizeContext } from "./ModalSizeContext";
import { clickWasOnModalView, modalClassName } from "./utils";

const isIos = preprocessorQuery.isIos();
const isMobile = preprocessorQuery.isMobile();

const getContainerContext = memoizeOne((containerRect, paddingLeft, paddingRight) => {
  let result = { containerRect };
  if (isMobile) {
    result.paddingLeft = paddingLeft;
    result.paddingRight = paddingRight;
  }
  return result;
});

const getIosModalSizeContext = memoizeOne(maxWrapperHeight => {
  return { maxHeight: maxWrapperHeight, fixedHeight: maxWrapperHeight };
});

const getNormalModalSizeContext = memoizeOne((maxWrapperHeight, fixedWrapperHeight) => {
  return { maxHeight: maxWrapperHeight, fixedHeight: fixedWrapperHeight };
});

class ModalView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this._domNode = null;
    this.state = {
      left: null,
      right: null,
      top: null,
      bottom: null,
      width: null,
      height: null
    };
    this.updateViewDimensions = this.updateViewDimensions.bind(this);
  }

  componentDidMount() {
    this.updateViewDimensions();
  }

  componentDidUpdate() {
    this.updateViewDimensions();
  }

  updateViewDimensions() {
    if (!this._domNode) {
      return;
    }
    const domRect = this._domNode.getBoundingClientRect();
    let plainRect = {
      left: domRect.left,
      right: domRect.right,
      width: domRect.width,
      top: domRect.top,
      bottom: domRect.bottom,
      height: domRect.height
    };

    if (
      plainRect.left !== this.state.left ||
      plainRect.width !== this.state.width ||
      plainRect.top !== this.state.top ||
      plainRect.height !== this.state.height
    ) {
      // In mobile mode, the modal view does not have X padding
      const paddingX = isMobile ? 0 : Dimensions.MODAL_X_PADDING;
      plainRect.containerRect = immutableRecords.FlatRectRecord({
        left: plainRect.left + paddingX,
        right: plainRect.right - paddingX,
        width: plainRect.width - 2 * paddingX,
        top: plainRect.top + Dimensions.MODAL_Y_PADDING,
        bottom: plainRect.bottom - Dimensions.MODAL_Y_PADDING,
        height: plainRect.height - 2 * Dimensions.MODAL_Y_PADDING
      });

      this.setState(plainRect);
    }
  }

  getContainerLimitHeight() {
    let height = this.props.containerRect.height;
    //#!if mobile !== true && harvard !== true
// height -= Dimensions.MODAL_TOP + Dimensions.MODAL_BOTTOM;
    //#!endif
    return height;
  }

  getFixedHeight(containerLimitHeight) {
    let height = containerLimitHeight;
    //#!if mobile !== true && harvard !== true
// height = this.props.fixedHeight ? Math.min(containerLimitHeight, this.props.fixedHeight) : containerLimitHeight;
    //#!endif
    return height;
  }

  getMaxHeight(isAutoHeight, containerLimitHeight, fixedHeight) {
    return isAutoHeight ? Math.min(Dimensions.MODAL_MAX_HEIGHT, containerLimitHeight) : fixedHeight;
  }

  // Note: heightAdjust is not correct in mobile inspector modal
  // since the header includes toolbar and normal heading.
  // Now we do not rely on the calculated height but using the
  // css flex to auto adjust.
  getHeightAdjust() {
    let adjustHeight = 0;
    //#!if mobile !== true && harvard !== true
// adjustHeight = isMobile
// ? (this.props.header ? Dimensions.MODAL_HEADING_HEIGHT : 0) +
// (this.props.footer ? Dimensions.MODAL_FOOTER_HEIGHT : 0)
// : 2 * (Dimensions.MODAL_Y_PADDING + Dimensions.MODAL_Y_BORDER_WIDTH) +
// (this.props.header ? Dimensions.MODAL_HEADING_HEIGHT : 0) +
// (this.props.footer ? Dimensions.MODAL_FOOTER_HEIGHT : 0);
    //#!endif
    return adjustHeight;
  }

  getModalContent(maxWrapperHeight, fixedWrapperHeight) {
    let modalContent = this.props.children;
    if (this.state.width) {
      const style = {
        maxHeight: `${maxWrapperHeight}px`,
        height: fixedWrapperHeight ? `${fixedWrapperHeight}px` : null
      };

      const containerContext = getContainerContext(
        this.state.containerRect,
        this.props.paddingLeft,
        this.props.paddingRight
      );

      modalContent = (
        <ContainerContext.Provider value={containerContext}>
          {this.props.header}
          <SmartErrorBoundary>
            <div className="modalMeasure" style={style}>
              {this.props.children}
            </div>
          </SmartErrorBoundary>
          {this.props.footer}
        </ContainerContext.Provider>
      );
    }
    return modalContent;
  }

  getIosComponent(viewStyle, maxWrapperHeight, modalContent) {
    const contextValue = getIosModalSizeContext(maxWrapperHeight);
    const wrapperComponent = (
      <div
        className={`iosModalContentWrap ${this.props.className ? this.props.className : ""}`}
        ref={node => (this._domNode = node)}
        style={viewStyle}
      >
        <ModalSizeContext.Provider value={contextValue}>{modalContent}</ModalSizeContext.Provider>
      </div>
    );

    return asOptionalPortalChild(wrapperComponent, platformIdentifiers.IOS);
  }

  getNormalComponent(viewStyle, maxWrapperHeight, fixedWrapperHeight, modalContent) {
    const onBackdropClick = this.props.onBackdropClick
      ? e => (!clickWasOnModalView(e) ? this.props.onBackdropClick(e) : null)
      : null;

    const contextValue = getNormalModalSizeContext(maxWrapperHeight, fixedWrapperHeight);

    const modalComponent = (
      <div
        className={`${modalClassName} ${this.props.className ? this.props.className : ""}`}
        ref={node => (this._domNode = node)}
        style={viewStyle}
      >
        <ModalSizeContext.Provider value={contextValue}>{modalContent}</ModalSizeContext.Provider>
      </div>
    );

    return this.props.disableFocusLock ? (
      <div className="modalFocusLockWrapper slidePage">
        <div className={"modalBackdrop"} onClick={onBackdropClick}>
          {AppShared.wrapInDragDropContainer(modalComponent)}
        </div>
      </div>
    ) : (
      <div className="modalFocusLockWrapper slidePage">
        <FocusLock>
          <div className={"modalBackdrop"} onClick={onBackdropClick}>
            {AppShared.wrapInDragDropContainer(modalComponent)}
          </div>
        </FocusLock>
      </div>
    );
  }

  render() {
    const isAutoHeight = !this.props.fixedHeight;
    const containerLimitHeight = this.getContainerLimitHeight();
    const fixedHeight = this.getFixedHeight(containerLimitHeight);
    const maxHeight = this.getMaxHeight(isAutoHeight, containerLimitHeight, fixedHeight);

    const heightAdjust = this.getHeightAdjust();

    const maxWrapperHeight = maxHeight - heightAdjust;
    const fixedWrapperHeight = isAutoHeight ? null : fixedHeight - heightAdjust;

    const modalContent = this.getModalContent(maxWrapperHeight, fixedWrapperHeight);

    const viewStyle = {
      maxHeight: `${maxHeight}px`,
      height: isAutoHeight ? null : `${fixedHeight}px`
    };

    const component = isIos
      ? this.getIosComponent(viewStyle, maxWrapperHeight, modalContent)
      : this.getNormalComponent(viewStyle, maxWrapperHeight, fixedWrapperHeight, modalContent);

    return component;
  }
}

ModalView.propTypes = {
  className: PropTypes.string,
  children: PropTypeShapes.elementOrArray.isRequired,
  header: PropTypes.element,
  footer: PropTypes.element,
  fixedHeight: PropTypes.number,
  onBackdropClick: PropTypes.func,
  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number,
  disableFocusLock: PropTypes.bool,
  // From screenContext
  screenContext: PropTypes.object
};

export default withScreenContext(withContainerContext(ModalView));
